import { Configuration, PopupRequest, IPublicClientApplication, AccountInfo } from '@azure/msal-browser';

// Define deployment config interface
interface DeploymentConfig {
  authentication?: {
    tenant_id: string;
    client_id: string;
    redirect_uri: string;
    api_scope: string;
  };
}

// Extend window interface
declare global {
  interface Window {
    deploymentConfig?: DeploymentConfig;
  }
}

// Load configuration from deployment config or fallback to hardcoded values
const loadConfig = () => {
  // Try to load from window.deploymentConfig if available (injected by build)
  const deploymentConfig = window.deploymentConfig;
  
  if (deploymentConfig?.authentication) {
    return deploymentConfig.authentication;
  }
  
  // Get redirect URI - must be computed at RUNTIME, not build time
  // process.env values are replaced at build time, so we need to check explicitly
  const envRedirectUri = process.env.REACT_APP_REDIRECT_URI;
  const redirectUri = (envRedirectUri && envRedirectUri.length > 0 && !envRedirectUri.includes('localhost'))
    ? envRedirectUri 
    : (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000');
  
  // Fallback to environment variables or hardcoded values
  return {
    tenant_id: process.env.REACT_APP_TENANT_ID || '7b95e31b-8675-4ce7-9dce-e5ee14ca323c',
    client_id: process.env.REACT_APP_CLIENT_ID || '35c22bd2-42c7-452a-b38d-5509e9f44ad7',
    redirect_uri: redirectUri,
    api_scope: process.env.REACT_APP_API_SCOPE || 'api://35c22bd2-42c7-452a-b38d-5509e9f44ad7'
  };
};

const authConfig = loadConfig();

// Validate configuration to prevent undefined values
if (!authConfig.client_id || authConfig.client_id === 'undefined') {
  console.error('⚠️ CRITICAL: client_id is undefined or invalid');
  console.error('Environment variables:', {
    REACT_APP_CLIENT_ID: process.env.REACT_APP_CLIENT_ID,
    REACT_APP_TENANT_ID: process.env.REACT_APP_TENANT_ID,
    REACT_APP_REDIRECT_URI: process.env.REACT_APP_REDIRECT_URI,
    REACT_APP_API_SCOPE: process.env.REACT_APP_API_SCOPE
  });
}

if (!authConfig.api_scope || authConfig.api_scope === 'undefined' || authConfig.api_scope.includes('undefined')) {
  console.error('⚠️ CRITICAL: api_scope contains undefined');
  console.error('Current api_scope:', authConfig.api_scope);
}

// Debug: Log authentication configuration
// console.log('🔧 Auth Config Loaded:', {
//   client_id: authConfig.client_id?.substring(0, 8) + '...',
//   tenant_id: authConfig.tenant_id?.substring(0, 8) + '...',
//   redirect_uri: authConfig.redirect_uri,
//   api_scope: authConfig.api_scope,
//   window_origin: window.location.origin
// });

// MSAL configuration - Fixed for consumer Microsoft accounts
export const msalConfig: Configuration = {
  auth: {
    clientId: authConfig.client_id,
    // Use 'common' authority for consumer Microsoft accounts
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: authConfig.redirect_uri,
    postLogoutRedirectUri: authConfig.redirect_uri,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: true,
  },
  system: {
    // Reduce iframe timeout to fail faster and fallback to popup
    windowHashTimeout: 10000, // 10 seconds instead of default 60 seconds
    iframeHashTimeout: 10000, // 10 seconds for iframe operations
    // Prevent redirects inside iframes to avoid CSP issues
    allowRedirectInIframe: false,
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case 0:
            console.error('MSAL Error:', message);
            break;
          case 1:
            console.warn('MSAL Warning:', message);
            break;
          case 2:
            console.info('MSAL Info:', message);
            break;
          case 3:
            console.log('MSAL Debug:', message);
            break;
        }
      },
      logLevel: 2,
    },
  },
};

// Login request with basic scopes only (no custom API scopes for multi-tenant support)
export const loginRequest: PopupRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
  extraScopesToConsent: undefined, // Remove API scope from initial login
};

// Add the endpoints here for Microsoft Graph API services you'd like to use.
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
};

// API configuration - Enhanced for multi-tenant support
export const apiConfig = {
  baseUrl: process.env.REACT_APP_API_BASE_URL || 'https://pottery-catalog-dev-func.azurewebsites.net/api',
  // Use basic scopes for all users by default - API will validate tokens accordingly
  scopes: ['openid', 'profile', 'email', 'User.Read'],
  clientId: authConfig.client_id,
  // Store the custom API scope for reference but don't use it for external tenants
  customApiScope: authConfig.api_scope,
  appTenantId: authConfig.tenant_id,
  // Shopify store handle for admin deep linking
  shopifyStoreHandle: process.env.REACT_APP_SHOPIFY_STORE_HANDLE || '88wtfh-jv'
};

// Helper function to check if user is from the app registration tenant
const isUserFromAppTenant = (account: AccountInfo): boolean => {
  // Check if user's tenant matches the configured tenant
  return account.tenantId === authConfig.tenant_id;
};

// Helper function to detect errors that should skip silent token retry and go directly to popup
// Includes iframe timeouts and user cancellation which both require interactive authentication
const shouldUsePopupFallback = (error: unknown): boolean => {
  const errorCode = (error as { errorCode?: string })?.errorCode;
  return errorCode === 'monitor_window_timeout' ||  // Iframe timeout
         errorCode === 'block_token_requests' ||     // Blocked by browser/CSP
         errorCode === 'user_cancelled';             // User explicitly cancelled, needs popup
};

// Helper function to get token for API calls with multi-tenant support
export const getApiToken = async (instance: IPublicClientApplication, account: AccountInfo) => {
  console.log('🔍 User tenant:', account.tenantId);
  console.log('🔍 App tenant:', authConfig.tenant_id);
  
  const isFromAppTenant = isUserFromAppTenant(account);
  console.log('🔍 User is from app tenant:', isFromAppTenant);
  
  // For users from other tenants, only use basic scopes
  const scopes = isFromAppTenant && authConfig.api_scope?.includes('api://') 
    ? [authConfig.api_scope] 
    : ['openid', 'profile', 'email', 'User.Read'];
  
  const tokenRequest = {
    scopes: scopes,
    account: account
  };

  try {
    console.log('🔑 Attempting to acquire token with scopes:', tokenRequest.scopes);
    const response = await instance.acquireTokenSilent(tokenRequest);
    
    // For custom API scopes, prefer access token; for basic scopes, use ID token
    const token = (isFromAppTenant && authConfig.api_scope?.includes('api://')) 
      ? response.accessToken 
      : (response.idToken || response.accessToken);
      
    console.log('✅ Token acquired successfully');
    console.log('🔍 Token type:', (isFromAppTenant && authConfig.api_scope?.includes('api://')) ? 'access' : 'id/access');
    return token;
  } catch (error) {
    // Check for errors that require popup fallback (iframe timeouts, blocked requests, user cancellation)
    const needsPopupFallback = shouldUsePopupFallback(error);
    
    if (needsPopupFallback) {
      const errorCode = (error as { errorCode?: string })?.errorCode;
      console.warn('⚠️ Silent token acquisition requires popup fallback:', errorCode);
    } else {
      console.warn('⚠️ Silent token acquisition failed:', error);
    }
    
    // For users from app tenant, try fallback to basic scopes if custom API scope fails
    // Skip this step for errors that require popup to go directly to interactive authentication
    if (!needsPopupFallback && isFromAppTenant && authConfig.api_scope?.includes('api://')) {
      console.log('🔄 Retrying with basic scopes (fallback for app tenant user)...');
      const fallbackTokenRequest = {
        scopes: ['openid', 'profile', 'email', 'User.Read'],
        account: account
      };
      
      try {
        const response = await instance.acquireTokenSilent(fallbackTokenRequest);
        console.log('✅ Fallback token acquired successfully');
        return response.idToken || response.accessToken;
      } catch (fallbackError) {
        const fallbackNeedsPopup = shouldUsePopupFallback(fallbackError);
        if (fallbackNeedsPopup) {
          const fallbackErrorCode = (fallbackError as { errorCode?: string })?.errorCode;
          console.warn('⚠️ Fallback also requires popup, proceeding to interactive auth:', fallbackErrorCode);
        } else {
          console.warn('⚠️ Fallback silent token acquisition failed:', fallbackError);
        }
      }
    }
    
    // If silent acquisition fails, try popup with basic scopes only
    const popupTokenRequest = {
      scopes: ['openid', 'profile', 'email', 'User.Read'],
      account: account
    };
    
    try {
      console.log('🔄 Attempting popup token acquisition with basic scopes...');
      const response = await instance.acquireTokenPopup(popupTokenRequest);
      console.log('✅ Popup token acquired successfully');
      return response.idToken || response.accessToken;
    } catch (popupError) {
      console.error('❌ All token acquisition attempts failed:', popupError);
      const popupErrorMessage = popupError instanceof Error ? popupError.message : String(popupError);
      throw new Error(`Authentication failed: ${popupErrorMessage}`);
    }
  }
};